<script setup lang="ts">
import { computed, useTemplateRef } from "vue";

import { RiskBoard } from "@/board/RiskBoard";
import { RiskBoardLocation } from "@/board/location/RiskBoardLocation";
import StickyNote from "@/components/StickyNote/StickyNote.vue";
import { relativeClientCoord } from "@/math/coordinate-utils";
import { normalLinkColors } from "@/model/colors";
import type { RelativeCoordinate } from "@/model/coordinates";
import { useBoardsStore } from "@/store/boards";
import { blurHandlerElem } from "@/utils/dom/dom";

import LoadingIndicator from "../ui/LoadingIndicator.vue";
import LinkLayers from "./internal/LinkLayer/LinkLayers.vue";
import { useFluidBoard } from "./internal/useFluidBoard";

const board = computed(() => useBoardsStore().boardByType("risk"));
const rootRef = useTemplateRef("root");

const { active, isLoading, levelOfDetails, focusAndBlur } = useFluidBoard(board, rootRef);

defineExpose(focusAndBlur);

function location(c: RelativeCoordinate | number) {
  return RiskBoardLocation.of(c);
}

function zoomToRegionHandler(e: MouseEvent) {
  const boardLogic = new RiskBoard(board.value);
  const loc = location(relativeClientCoord(e));
  boardLogic.zoomAction(loc)?.("mouse");
}
</script>

<!-- eslint-disable vuejs-accessibility/click-events-have-key-events vuejs-accessibility/no-static-element-interactions-->
<template>
  <div
    :id="board.id"
    ref="root"
    class="board board-risk"
    :aria-label="$t('board.risk')"
    tabindex="0"
    @click="blurHandlerElem"
  >
    <LoadingIndicator v-if="active && isLoading" global />
    <LinkLayers :board :color="normalLinkColors.risk" />
    <div class="backdrop risk-board-grid" @dblclick="zoomToRegionHandler">
      <section
        v-board-region
        v-owned-cards="{ board, location: location(0) }"
        class="field left"
        aria-labelledby="risks-heading-0"
        @click="blurHandlerElem"
      >
        <h2 id="risks-heading-0" class="h3" tabindex="-1">
          {{ location(0).name }}
        </h2>
      </section>
      <section
        v-board-region
        v-owned-cards="{ board, location: location(1) }"
        class="field right"
        aria-labelledby="risks-heading-1"
        @click="blurHandlerElem"
      >
        <h2 id="risks-heading-1" class="h3" tabindex="-1">
          {{ location(1).name }}
        </h2>
      </section>
      <section
        v-board-region
        v-owned-cards="{ board, location: location(2) }"
        class="field left"
        aria-labelledby="risks-heading-2"
        @click="blurHandlerElem"
      >
        <h2 id="risks-heading-2" class="h3" tabindex="-1">
          {{ location(2).name }}
        </h2>
      </section>
      <section
        v-board-region
        v-owned-cards="{ board, location: location(3) }"
        class="field right"
        aria-labelledby="risks-heading-3"
        @click="blurHandlerElem"
      >
        <h2 id="risks-heading-3" class="h3" tabindex="-1">
          {{ location(3).name }}
        </h2>
      </section>
      <section
        v-board-region
        v-owned-cards="{ board, location: location(4) }"
        class="field full"
        aria-labelledby="risks-heading-4"
        @click="blurHandlerElem"
      >
        <h2 id="risks-heading-4" class="h3" tabindex="-1">
          {{ location(4).name }}
        </h2>
      </section>
    </div>
    <StickyNote
      v-for="card in board.cards"
      :key="card.data.id"
      :card="card.data"
      :card-meta="card.meta"
      :level-of-details
    />
  </div>
</template>

<style scoped lang="scss">
@use "@/styles/mixins/a11y";
@use "@/styles/board";

.board {
  @include a11y.board;

  .risk-board-grid {
    display: grid;
    grid-template-columns: 50% 50%;
    grid-template-rows: 50% 35% 15%;
  }

  // Define section positions
  .field {
    @include a11y.board-section;

    position: relative;

    // Header positions are a overly-specific to match current percentage-based positions
    > h2 {
      @include board.board-control;

      position: absolute;
      top: 2.7em;
    }

    &.left > h2 {
      left: 3.2em;
    }

    &.right > h2 {
      right: 3.2em;
    }

    &.full {
      grid-column: 1 / span 2;

      > h2 {
        top: 1em;
        left: 3.2em;
      }
    }
  }
}
</style>
